import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormControl, Validators } from '@angular/forms';
import { RowComponent, ColComponent, CardComponent, CardHeaderComponent, CardBodyComponent, SpinnerComponent } from '@coreui/angular';
import { IconModule } from '@coreui/icons-angular';
import { ClientService } from '../../../services/client.service';
import { ToastrService } from 'ngx-toastr';

interface ClientApprover {
  id?: number;
  client_code: string;
  first_name: string;
  last_name: string;
  title?: string;
  email: string;
  status: string;
  created_at?: string;
}

@Component({
  selector: 'app-client-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RowComponent,
    ColComponent,
    CardComponent,
    CardHeaderComponent,
    CardBodyComponent,
    SpinnerComponent,
    IconModule
  ],
  templateUrl: './client-form.component.html',
  styleUrls: ['./client-form.component.scss']
})
export class ClientFormComponent implements OnInit {
  errors: { [key: string]: string[] } = {};

  client: {
    id?: number;
    client_code: string;
    status: string;
    client_name: string;
    direct_supervisor_name: string;
    website?: string;
    contact_email_primary: string;
    contact_first_name_primary: string;
    contact_last_name_primary: string;
    phone_primary: string;
    contact_email_secondary?: string;
    contact_first_name_secondary?: string;
    contact_last_name_secondary?: string;
    phone_secondary?: string;
    contract_start_date?: string;
    end_of_contract_date?: string;
    email_delivery_method?: string;
    timesheet_link?: string;
    timesheet_last_sent?: string;
  } = {
    id: undefined,
    client_code: '',
    status: 'Newly Onboard',
    client_name: '',
    direct_supervisor_name: '',
    website: '',
    contact_email_primary: '',
    contact_first_name_primary: '',
    contact_last_name_primary: '',
    phone_primary: '',
    contact_email_secondary: '',
    contact_first_name_secondary: '',
    contact_last_name_secondary: '',
    phone_secondary: '',
    contract_start_date: '',
    end_of_contract_date: '',
    email_delivery_method: '',
    timesheet_link: '',
    timesheet_last_sent: '',
  };
  isEditMode = false;

  // Approvers
  approvers: ClientApprover[] = [];
  loadingApprovers = false;
  showApproverForm = false;
  editingApprover: ClientApprover | null = null;
  savingApprover = false;

  approverForm = new FormGroup({
    first_name: new FormControl('', [Validators.required]),
    last_name:  new FormControl('', [Validators.required]),
    title:      new FormControl(''),
    email:      new FormControl('', [Validators.required, Validators.email]),
    status:     new FormControl('active', [Validators.required])
  });

  constructor(
    private router: Router,
    private clientService: ClientService,
    private toastr: ToastrService
  ) {
    const navigation = this.router.getCurrentNavigation();
    const stateClient = navigation?.extras.state?.['client'];
    if (stateClient) {
      this.client = { ...stateClient };
      this.isEditMode = true;
    }
  }

  ngOnInit(): void {
    if (this.isEditMode) {
      this.loadApprovers();
    }
  }

  loadApprovers() {
    this.loadingApprovers = true;
    this.clientService.getApprovers({ client_code: this.client.client_code }).subscribe({
      next: (res: any) => {
        this.approvers = res?.data ?? res ?? [];
        this.loadingApprovers = false;
      },
      error: () => {
        this.approvers = [];
        this.loadingApprovers = false;
      }
    });
  }

  openAddApprover() {
    this.editingApprover = null;
    this.approverForm.reset({ status: 'active' });
    this.showApproverForm = true;
  }

  openEditApprover(approver: ClientApprover) {
    this.editingApprover = approver;
    this.approverForm.patchValue({ first_name: approver.first_name, last_name: approver.last_name, title: approver.title ?? '', email: approver.email, status: approver.status });
    this.showApproverForm = true;
  }

  cancelApproverForm() {
    this.showApproverForm = false;
    this.editingApprover = null;
  }

  saveApprover() {
    if (this.approverForm.invalid) {
      this.approverForm.markAllAsTouched();
      return;
    }
    this.savingApprover = true;
    const value: any = { ...this.approverForm.value, client_code: this.client.client_code };

    const request = this.editingApprover
      ? this.clientService.updateApprover(this.editingApprover.id!, value)
      : this.clientService.createApprover(value);

    request.subscribe({
      next: () => {
        this.toastr.success(this.editingApprover ? 'Approver updated.' : 'Approver added.', 'Success');
        this.savingApprover = false;
        this.cancelApproverForm();
        this.loadApprovers();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message ?? 'An error occurred.', 'Error');
        this.savingApprover = false;
      }
    });
  }

  deleteApprover(approver: ClientApprover) {
    if (!confirm(`Delete approver "${approver.first_name} ${approver.last_name}"?`)) return;
    this.clientService.deleteApprover(approver.id!).subscribe({
      next: () => {
        this.toastr.success('Approver deleted.', 'Success');
        this.loadApprovers();
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.message ?? 'Failed to delete.', 'Error');
      }
    });
  }

  onSubmit() {
    this.errors = {};
    if (this.isEditMode) {
      if (typeof this.client.id === 'number') {
        this.clientService.updateClient(this.client.id, this.client).subscribe({
          next: () => this.router.navigate(['/clients']),
          error: (err) => {
            if (err?.error?.errors) {
              this.errors = err.error.errors;
            } else {
              alert('Failed to update client');
            }
          }
        });
      } else {
        alert('Client ID is missing. Cannot update.');
      }
    } else {
      this.clientService.createClient(this.client).subscribe({
        next: () => this.router.navigate(['/clients']),
        error: (err) => {
          if (err?.error?.errors) {
            this.errors = err.error.errors;
          } else {
            alert('Failed to create client');
          }
        }
      });
    }
  }
}
